import { z } from 'zod';
import { IdSchema } from './common.ts';
import {
  MoneySchema,
  addMoney,
  money,
  multiplyMoney,
  subtractMoney,
  sumMoney,
  type Money,
} from '../money.ts';
import { CostTypeSchema, MAX_MARKUP_MILLIONTHS } from './cost-code.ts';

/**
 * One priced line, and the only line shape Phase 5 has.
 *
 * An estimate, a budget line's source rows, a purchase order, a bill, a
 * receipt and an owner invoice all carry these. Writing the shape six times
 * is how five of them end up rounding differently, and the sixth is the one
 * a customer notices.
 *
 * **Three conventions, from `AGENTS.md` §3, and this is where every later
 * table gets them:**
 *
 * - **Money is integer minor units** with a currency. Never float. A penny
 *   of float error per line is a dollar across a job and an argument across
 *   a portfolio.
 * - **Quantity is thousandths.** `2.5` hours is `2500`. The multiplication
 *   is then integer times integer and rounds once, at the end, rather than
 *   compounding a float per line.
 * - **Markup is in millionths**: the value is divided by 1e6, so 20% is
 *   `200_000` and 100% is `1_000_000`. Equivalently a ten-thousandth of a
 *   percent per unit — a hundred units to the basis point, which is why it
 *   is not called one. The
 *   field is called `markupMillionths` to match `CoLine`, and that name is
 *   wrong by a factor of a hundred — see the note on `CoLine` and P5-00,
 *   which is the rename. Use `markupFromPercent`, never a literal.
 *
 * **Everything derivable is derived.** `builderCost`, the markup in money,
 * `ownerPrice` and the margin are functions, not fields, even though
 * `docs/DATA_MODEL.md` §6 originally listed them as stored. A stored total
 * is a total that eventually disagrees with its lines, and the
 * disagreement is discovered by a customer reading an invoice. The same
 * decision was already made for `coTotals`.
 */

/**
 * Where a line came from.
 *
 * Provenance, not a live link: the values on the line are copies, and this
 * says what they were copied from so a budget row can offer to open it. A
 * bill line made from a purchase order line keeps pointing at that line
 * even after the purchase order is edited, because what was billed is a
 * fact about the past.
 */
export const CostLineSourceSchema = z.object({
  collection: z.string().min(1),
  id: IdSchema,
  lineId: IdSchema.optional(),
});
export type CostLineSource = z.infer<typeof CostLineSourceSchema>;

export const CostLineSchema = z.object({
  id: IdSchema,
  /** What it is, in the words that go on the document somebody signs. */
  title: z.string().trim().min(1).max(200),
  description: z.string().trim().max(2_000).optional(),
  /**
   * The code this line is costed against.
   *
   * Optional, and the reason is a real workflow rather than laziness: a
   * line typed quickly during a site visit has a price before it has a
   * code, and refusing the write would mean losing the price. A line with
   * no code rolls up under "Uncoded" on the budget, where it is visible
   * and fixable — `jobCosting` is where that happens.
   */
  costCodeId: IdSchema.optional(),
  costType: CostTypeSchema.optional(),
  /** Thousandths: 2.5 is 2500. Positive — a zero line is one to delete. */
  quantityThousandths: z.number().int().positive(),
  unit: z.string().trim().max(20).optional(),
  /** The builder's cost for one unit, before any markup. */
  unitCost: MoneySchema,
  /**
   * Millionths: 20% is 200_000. The ceiling is
   * `MAX_MARKUP_MILLIONTHS` — 1000%, and the reasoning for the figure
   * is on it. A typo here reaches a customer as a price, which is why
   * there is a ceiling at all; the UI asks for confirmation above 100%,
   * where a plausible typo lives.
   */
  markupMillionths: z.number().int().min(0).max(MAX_MARKUP_MILLIONTHS).default(0),
  /** Whether tax applies. The rate is a company record, not a line field. */
  taxable: z.boolean().default(false),
  /**
   * What the owner pays per unit, instead of cost plus markup.
   *
   * Two things need this and they are the same thing: a catalog item
   * with a quoted rate ("connectors are $2.50, whatever we paid"), and
   * a project manager overriding a price on a job's bill. So it is one
   * field rather than two, and `lineOwnerPrice` prefers it when it is
   * there.
   *
   * **It cannot be expressed as a markup**, which is why it exists. A
   * connector bought at 0.42 and sold at 2.50 is 495%; storing that as
   * a percentage of a cost that moves means the quoted price drifts the
   * next time the cost does, and a quote that drifts is not a quote.
   *
   * **Absent is not zero.** `undefined` means "use cost plus markup";
   * `{ amount: 0 }` means somebody is giving this away. `Money` being
   * an object is what keeps those apart — a nullable number could not.
   *
   * **Copied from the catalog onto the line, never written back.** The
   * catalog's rule is that changing an item changes what the *next*
   * line copies and nothing already written; this is that rule in the
   * other direction, and it is the first field where both directions
   * matter.
   */
  unitPriceOverride: MoneySchema.optional(),
  /** Set when the line came from the catalog, as provenance. */
  catalogItemId: IdSchema.optional(),
  sourceRef: CostLineSourceSchema.optional(),
});
export type CostLine = z.infer<typeof CostLineSchema>;

/** A line's builder cost: quantity times unit cost, rounded once. */
export function lineBuilderCost(line: Pick<CostLine, 'quantityThousandths' | 'unitCost'>): Money {
  return multiplyMoney(line.unitCost, line.quantityThousandths / 1000);
}

/**
 * A line's markup, in money.
 *
 * Rounded **half away from zero** by `multiplyMoney`, which means a markup
 * of exactly half a cent goes up. That is the rule most builders' own
 * spreadsheets use, and the alternative — banker's rounding — surprises
 * people who checked the arithmetic by hand. Written down here because
 * "which way does the half cent go" is the question somebody eventually
 * asks about an invoice, and the answer should not require reading
 * `multiplyMoney`.
 *
 * The markup is taken on the line's **whole** cost, not per unit. Twenty
 * per cent on three units at $3.33 is 20% of $9.99, not three lots of 20%
 * of $3.33 — which differ by a cent, and the first is what a person
 * checking the total expects.
 */
export function lineMarkupAmount(
  line: Pick<
    CostLine,
    'quantityThousandths' | 'unitCost' | 'markupMillionths' | 'unitPriceOverride'
  >,
): Money {
  /**
   * **With an override, the markup is whatever is left over**, and
   * keeping that true is the whole reason this branch is here rather
   * than only in `lineOwnerPrice`.
   *
   * `CostTotals` reports cost, markup and price together, and the
   * budget reads all three. If the price came from an override and the
   * markup did not, the three would stop adding up — a total that does
   * not equal the column above it is the single most common complaint
   * about estimating software, and it is noted elsewhere in this file as
   * exactly the thing to avoid.
   *
   * It can be **negative**, when somebody prices below cost. That is a
   * real thing a builder does as a gesture, and reporting it as a loss
   * is more useful than clamping it to zero and hiding it.
   */
  if (line.unitPriceOverride) {
    return subtractMoney(overriddenPrice(line), lineBuilderCost(line));
  }
  return multiplyMoney(lineBuilderCost(line), line.markupMillionths / 1_000_000);
}

/** The override taken per unit, so three units at $2.50 is $7.50. */
function overriddenPrice(line: Pick<CostLine, 'quantityThousandths' | 'unitPriceOverride'>): Money {
  return multiplyMoney(line.unitPriceOverride!, line.quantityThousandths / 1000);
}

/**
 * What the owner pays for this line: cost plus markup, or the override.
 *
 * **A single chokepoint, deliberately.** Four core modules and the web
 * app compute price through this one function, so honouring the
 * override here reaches the budget, job costing, change orders and the
 * owner invoice at once — and a line without one behaves exactly as it
 * did before this field existed.
 */
export function lineOwnerPrice(
  line: Pick<
    CostLine,
    'quantityThousandths' | 'unitCost' | 'markupMillionths' | 'unitPriceOverride'
  >,
): Money {
  if (line.unitPriceOverride) return overriddenPrice(line);
  return addMoney(lineBuilderCost(line), lineMarkupAmount(line));
}

/**
 * A line's margin as a percentage of the owner price, to two decimals.
 *
 * **Margin, not markup**, and the difference catches people out: a 20%
 * markup is a 16.67% margin, because markup is over cost and margin is
 * over price. Shown as a number rather than a `Money` because it is a
 * ratio, and returned as a float because it is for display only — nothing
 * is ever computed from it.
 *
 * Zero when the owner price is zero, rather than `NaN`. A free line has no
 * margin, and `NaN` on a budget page is worse than a zero.
 */
export function lineMarginPercent(
  line: Pick<
    CostLine,
    'quantityThousandths' | 'unitCost' | 'markupMillionths' | 'unitPriceOverride'
  >,
): number {
  const price = lineOwnerPrice(line).amount;
  if (price === 0) return 0;
  const margin = (lineMarkupAmount(line).amount / price) * 100;
  return Math.round(margin * 100) / 100;
}

export interface CostTotals {
  readonly cost: Money;
  readonly markup: Money;
  readonly price: Money;
  /** The taxable part of the owner price, for a tax rate to apply to. */
  readonly taxableBase: Money;
}

/**
 * What a set of lines comes to.
 *
 * **Summed from the per-line figures, not recomputed from a total
 * quantity.** The two differ by rounding and only the first agrees with
 * what the lines display — a total that does not equal the column above it
 * is the single most common complaint about estimating software, and it
 * comes from exactly this choice.
 *
 * An empty set totals zero in the given currency rather than throwing. An
 * estimate with no lines yet is a normal state and a screen should show
 * `$0.00`, not an error.
 */
export function costTotals(lines: readonly CostLine[], currency = 'USD'): CostTotals {
  const cost = sumMoney(lines.map(lineBuilderCost), currency);
  const markup = sumMoney(lines.map(lineMarkupAmount), currency);
  return {
    cost,
    markup,
    price: addMoney(cost, markup),
    taxableBase: sumMoney(lines.filter((line) => line.taxable).map(lineOwnerPrice), currency),
  };
}

/**
 * Lines grouped by cost code, with their totals.
 *
 * The shape the budget needs: every figure on a job-costing row is a sum
 * over the lines costed against one code. A line with no code groups under
 * `null`, which the page shows as "Uncoded" — visible and fixable, rather
 * than silently excluded from a total somebody is reconciling.
 */
export interface CostLineGroup {
  readonly costCodeId: string | null;
  readonly lines: readonly CostLine[];
  readonly totals: CostTotals;
}

export function groupLinesByCostCode(
  lines: readonly CostLine[],
  currency = 'USD',
): readonly CostLineGroup[] {
  const byCode = new Map<string | null, CostLine[]>();
  for (const line of lines) {
    const key = line.costCodeId ?? null;
    const bucket = byCode.get(key);
    if (bucket) bucket.push(line);
    else byCode.set(key, [line]);
  }
  // Coded groups first in insertion order, then the uncoded one last —
  // where a reader looks for the thing that needs attention.
  const coded = [...byCode.entries()].filter(([key]) => key !== null);
  const uncoded = byCode.get(null);
  return [
    ...coded.map(([costCodeId, group]) => ({
      costCodeId,
      lines: group,
      totals: costTotals(group, currency),
    })),
    ...(uncoded
      ? [{ costCodeId: null, lines: uncoded, totals: costTotals(uncoded, currency) }]
      : []),
  ];
}

/**
 * The quantity a person typed, as thousandths.
 *
 * Here rather than in each form, so `2.5` means the same thing on an
 * estimate, a purchase order and a bill. Returns null for anything that is
 * not a positive number, because a line of zero is a line somebody meant
 * to delete and storing it would silently price nothing.
 */
export function quantityFromInput(text: string): number | null {
  const cleaned = text.trim().replace(/,/g, '');
  if (!/^\d*\.?\d+$/.test(cleaned)) return null;
  const quantity = Number.parseFloat(cleaned);
  if (!Number.isFinite(quantity) || quantity <= 0) return null;
  return Math.round(quantity * 1000);
}

/** Thousandths back to something to show a person: 2500 becomes "2.5". */
export function quantityToInput(thousandths: number): string {
  return String(thousandths / 1000);
}

/** A zero-value line, for a form that is about to be filled in. */
export function emptyCostLine(id: string, currency = 'USD'): CostLine {
  return {
    id,
    title: '',
    quantityThousandths: 1_000,
    unitCost: money(0, currency),
    markupMillionths: 0,
    taxable: false,
  };
}
